/**
 * Strategy Comparison MC Mock
 *
 * Side-by-side comparison of two strategies using Monte Carlo
 * metrics: CI width, P95 drawdown, profit probability, and VaR.
 */

const strategies = [
  {
    name: 'Strategy A',
    subtitle: 'EMA Cross',
    mean: '+14.8%',
    ci: '[+8%, +22%]',
    ciWidth: 14,
    p95dd: '-22%',
    p95ddNum: 22,
    profitProb: '92%',
    var: '-5.2%',
    color: '#3b82f6',
    verdict: 'Stable',
  },
  {
    name: 'Strategy B',
    subtitle: 'Momentum Breakout',
    mean: '+18.5%',
    ci: '[+2%, +38%]',
    ciWidth: 36,
    p95dd: '-41%',
    p95ddNum: 41,
    profitProb: '68%',
    var: '-12.8%',
    color: '#a855f7',
    verdict: 'Volatile',
  },
];

const metrics = ['Mean Return', '95% CI', 'P95 Drawdown', 'Profit Prob.', 'VaR (95%)'];

export default function StrategyComparisonMCMock() {
  return (
    <div className="platform-mock">
      <div className="platform-mock-header">
        <span className="dot" /> Strategy Comparison &mdash; Monte Carlo
      </div>
      <div className="platform-mock-body" style={{ padding: '0.75rem' }}>
        {/* Header row */}
        <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' }}>
          <div style={{ width: 80 }} />
          {strategies.map((s) => (
            <div key={s.name} style={{
              flex: 1, textAlign: 'center',
              background: `${s.color}10`,
              borderRadius: 4,
              padding: '0.3rem',
              border: `1px solid ${s.color}33`,
            }}>
              <div style={{ fontSize: '0.625rem', fontWeight: 700, color: s.color }}>{s.name}</div>
              <div style={{ fontSize: '0.4375rem', color: '#64748b' }}>{s.subtitle}</div>
            </div>
          ))}
        </div>

        {/* Metrics rows */}
        {metrics.map((metric, i) => {
          const values = strategies.map((s) => {
            switch (i) {
              case 0: return s.mean;
              case 1: return s.ci;
              case 2: return s.p95dd;
              case 3: return s.profitProb;
              case 4: return s.var;
              default: return '';
            }
          });

          // Determine which is "better" per metric
          const aBetter = i === 0 ? false : i === 1 ? true : i === 2 ? true : i === 3 ? true : true;

          return (
            <div key={metric} style={{
              display: 'flex', gap: '0.5rem', alignItems: 'center',
              padding: '0.25rem 0',
              borderBottom: i < metrics.length - 1 ? '1px solid #1e293b' : 'none',
            }}>
              <div style={{ width: 80, fontSize: '0.5rem', color: '#64748b' }}>{metric}</div>
              {values.map((v, j) => (
                <div key={j} style={{
                  flex: 1, textAlign: 'center',
                  fontSize: '0.625rem', fontWeight: 600,
                  color: aBetter === (j === 0) ? '#22c55e' : '#94a3b8',
                  fontFamily: 'ui-monospace, monospace',
                }}>
                  {v}
                </div>
              ))}
            </div>
          );
        })}

        {/* CI width comparison bar */}
        <div style={{ marginTop: '0.5rem' }}>
          <div style={{ fontSize: '0.4375rem', color: '#64748b', marginBottom: '0.25rem' }}>
            Confidence interval width (narrower = more predictable)
          </div>
          {strategies.map((s) => (
            <div key={s.name} style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', marginBottom: '0.25rem' }}>
              <span style={{ width: 50, fontSize: '0.5rem', color: s.color }}>{s.name}</span>
              <div style={{ flex: 1, height: 10, background: '#0f172a', borderRadius: 2, overflow: 'hidden' }}>
                <div style={{
                  height: '100%',
                  width: `${(s.ciWidth / 40) * 100}%`,
                  background: `${s.color}33`,
                  border: `1px solid ${s.color}55`,
                  borderRadius: 2,
                }} />
              </div>
              <span style={{ fontSize: '0.5rem', color: '#94a3b8', fontFamily: 'ui-monospace, monospace', width: 30, textAlign: 'right' }}>
                {s.ciWidth}%
              </span>
            </div>
          ))}
        </div>
      </div>
      <div className="platform-mock-caption">
        Strategy A has lower returns but tighter bands — more predictable and easier to size.
      </div>
    </div>
  );
}
